import { useMemo, useState } from "react";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";

const DEFAULT_REASONS = [
	"Submission unclear/incomplete",
	"Quality does not meet requirements",
	"Timing violation",
	"Duplicate submission",
	"Other",
] as const;

const OTHER = "Other";

interface Props {
	open: boolean;
	eventName?: string;
	loading?: boolean;
	onClose: () => void;
	onConfirm: (rejectionReason: string) => void;
}

export const RejectDialog = ({
	open,
	eventName,
	loading,
	onClose,
	onConfirm,
}: Props) => {
	const [reason, setReason] = useState<string>("");
	const [otherText, setOtherText] = useState("");

	const validationError = useMemo(() => {
		if (!reason) return "Select a reason";
		if (reason === OTHER) {
			const trimmed = otherText.trim();
			if (trimmed.length < 10)
				return "Provide at least 10 characters of detail";
			if (trimmed.length > 1000) return "Maximum 1000 characters";
		}
		return null;
	}, [reason, otherText]);

	const reset = (): void => {
		setReason("");
		setOtherText("");
	};

	const handleClose = (): void => {
		reset();
		onClose();
	};

	const handleConfirm = (): void => {
		if (validationError) return;
		const payload = reason === OTHER ? otherText.trim() : reason;
		onConfirm(payload);
		reset();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Reject submission</DialogTitle>
			<DialogContent>
				<DialogContentText sx={{ mb: 2 }}>
					Why are you rejecting this{" "}
					<strong>{eventName ?? "submission"}</strong>? Rejecting the submission
					rejects all components.
				</DialogContentText>
				<FormControl fullWidth size="small" sx={{ mb: 2 }}>
					<InputLabel>Reason</InputLabel>
					<Select
						label="Reason"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
					>
						{DEFAULT_REASONS.map((r) => (
							<MenuItem key={r} value={r}>
								{r}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				{reason === OTHER && (
					<TextField
						label="Reason detail"
						required
						fullWidth
						multiline
						rows={3}
						value={otherText}
						onChange={(e) => setOtherText(e.target.value)}
						inputProps={{ maxLength: 1000 }}
						helperText={`${otherText.trim().length}/1000 — minimum 10 characters`}
						error={Boolean(reason === OTHER && validationError)}
					/>
				)}
				{reason && reason !== OTHER && validationError && (
					<FormHelperText error>{validationError}</FormHelperText>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					variant="contained"
					color="error"
					onClick={handleConfirm}
					disabled={loading || Boolean(validationError)}
				>
					Confirm Rejection
				</Button>
			</DialogActions>
		</Dialog>
	);
};
